import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, functions, storage } from "../../firebase";
import { ACCOUNT_TYPES } from "../profileService";
import { toCapsuleSearchTerm } from "../../utils/timeCapsuleSearchTerm";

const TIME_CAPSULE_TYPES = ["private", "sponsored", "memberExclusive", "public"];
// Everything except "private". The list rule cannot check business membership
// (the roster lives in the studio database and rules cannot get() across
// databases), so a business-scoped or unfiltered query proves itself by
// pinning capsuleType away from private. Changes no results — business
// capsules are never private — it only makes that invariant provable.
export const NON_PRIVATE_CAPSULE_TYPES = ["sponsored", "public", "memberExclusive"];
const TIME_CAPSULE_STATUSES = ["draft", "published", "inactive"];
const MAX_HASHTAGS = 3;
const MAX_MEDIA_ITEMS = 12;
const MAX_VIDEO_ITEMS = 2;
const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_ASSIGNED_BUSINESS_ID = "CdjuOd6hge4ZCREVpu54";
const TIME_CAPSULE_COPY_SOURCES = ["default", "ai", "manual"];

const softDeleteDocumentCallable = httpsCallable(functions, "softDeleteDocument");
const restoreDocumentCallable = httpsCallable(functions, "restoreDocument");

const toPlain = (snap) => ({ id: snap.id, ...snap.data() });
const isPrivateCapsule = (row) => String(row?.capsuleType || "").trim() === "private";

function getCurrentUid() {
  try {
    return getAuth().currentUser?.uid || null;
  } catch {
    return null;
  }
}

function getActorContext(actor = null) {
  const uid = actor?.uid || getCurrentUid();
  const isAdmin = Boolean(actor?.isAdmin);

  let accountType = actor?.accountType || null;
  if (!accountType && typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem("userProfile");
      const profile = raw ? JSON.parse(raw) : null;
      const primaryBusinessId = String(profile?.primaryBusinessId || "").trim();
      const baseAccountType = profile?.accountType || null;
      accountType =
        baseAccountType === ACCOUNT_TYPES.INDIVIDUAL && primaryBusinessId
          ? ACCOUNT_TYPES.BUSINESS
          : baseAccountType;
      const actorBusinessId = String(actor?.businessId || "").trim();
      const downgradedBusinessIds = Array.isArray(profile?.downgradedBusinessIds)
        ? profile.downgradedBusinessIds
          .map((id) => String(id || "").trim())
          .filter(Boolean)
        : [];
      return {
        uid,
        isAdmin,
        accountType,
        businessId: actorBusinessId || primaryBusinessId || (isAdmin ? "" : DEFAULT_ASSIGNED_BUSINESS_ID),
        // downgradedBusinessId (singular) is a legacy scalar from profiles
        // written before a user could own more than one business; kept
        // alongside downgradedBusinessIds so both still gate correctly.
        downgradedBusinessId: String(profile?.downgradedBusinessId || "").trim() || null,
        downgradedBusinessIds,
      };
    } catch {
      accountType = null;
    }
  }

  const actorBusinessId = String(actor?.businessId || "").trim();
  if (accountType === ACCOUNT_TYPES.INDIVIDUAL && actorBusinessId) {
    accountType = ACCOUNT_TYPES.BUSINESS;
  }
  return {
    uid,
    isAdmin,
    accountType,
    businessId: actorBusinessId || (isAdmin ? "" : DEFAULT_ASSIGNED_BUSINESS_ID),
    downgradedBusinessId: null,
    downgradedBusinessIds: [],
  };
}

function assertCapsuleMutationAllowed(capsuleDoc, actor = null) {
  const { accountType, downgradedBusinessId, downgradedBusinessIds } =
    getActorContext(actor);
  const capsuleBusinessId = String(capsuleDoc?.businessId || "").trim();
  const isDowngradedBusiness =
    capsuleBusinessId &&
    (capsuleBusinessId === downgradedBusinessId ||
      (downgradedBusinessIds || []).includes(capsuleBusinessId));
  if (accountType === ACCOUNT_TYPES.INDIVIDUAL && isDowngradedBusiness) {
    throw new Error("Downgraded business time capsules are read-only until you upgrade again.");
  }
}

function assertCreateTypeAllowed(capsuleType, actor = null) {
  const { isAdmin, accountType } = getActorContext(actor);
  const normalizedType = String(capsuleType || "").trim();
  if (!TIME_CAPSULE_TYPES.includes(normalizedType)) {
    throw new Error("Invalid capsule type.");
  }

  if (isAdmin) {
    return;
  }

  if (accountType === ACCOUNT_TYPES.BUSINESS) {
    if (normalizedType === "private") {
      throw new Error("Business accounts cannot create private time capsules.");
    }
    return;
  }

  if (accountType === ACCOUNT_TYPES.INDIVIDUAL) {
    if (normalizedType !== "private") {
      throw new Error("Individual accounts can only create private time capsules.");
    }
    return;
  }

  throw new Error("You do not have permission to create time capsules.");
}

function normalizeHashtags(input = []) {
  const clean = (Array.isArray(input) ? input : [])
    .map((tag) => String(tag || "").trim().replace(/^#+/, ""))
    .filter(Boolean);
  return Array.from(new Set(clean)).slice(0, MAX_HASHTAGS);
}

function normalizeCtaText(value = "") {
  const raw = String(value || "");
  if (raw && /[^A-Za-z0-9 ]/.test(raw)) {
    throw new Error("Call to action text can only include letters, numbers, and spaces.");
  }
  return raw.replace(/\s+/g, " ").trim();
}

function normalizeCopySource(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  return TIME_CAPSULE_COPY_SOURCES.includes(normalized) ? normalized : "default";
}

function normalizeAiCopyBrief(value = {}) {
  return {
    missionGoals: String(value?.missionGoals || "").replace(/\s+/g, " ").trim(),
  };
}

function normalizeBusinessId(value = "") {
  const normalized = String(value || "").trim();
  return normalized || null;
}

// The stable `locationEntries[].id` (a `loc_N` value) a capsule is linked to,
// or null for a manual placement / a legacy business with no stable id to
// link to. Never inferred here — the caller decides the value; this only
// normalizes its shape.
function normalizeBusinessLocationId(value = null) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

function assertCanAccessCapsule(capsuleDoc, actor = null) {
  const { uid, isAdmin, accountType, businessId } = getActorContext(actor);
  if (!uid) throw new Error("You must be signed in.");

  const ownerUid = String(capsuleDoc?.senderId || capsuleDoc?.createdBy || "").trim();
  const isOwner = ownerUid && ownerUid === uid;
  const isPrivate = isPrivateCapsule(capsuleDoc);
  const capsuleBusinessId = String(capsuleDoc?.businessId || "").trim();

  if (isAdmin) {
    return;
  }

  if (accountType === ACCOUNT_TYPES.BUSINESS) {
    if (isPrivate) {
      throw new Error("Business users cannot access private time capsules.");
    }
    if (capsuleBusinessId && capsuleBusinessId === String(businessId || "").trim()) return;
    if (isOwner) return;
    throw new Error("Business users can only access time capsules for their active business.");
  }

  if (accountType === ACCOUNT_TYPES.INDIVIDUAL) {
    if (!isPrivate) {
      throw new Error("Individual users can only access private time capsules.");
    }
    if (isOwner) return;
    throw new Error("Individual users can only access their own private time capsules.");
  }

  throw new Error("You do not have access to time capsules.");
}

function canActorSeeCapsule(row, actor = null) {
  const { uid, isAdmin, accountType, businessId } = getActorContext(actor);
  if (!uid) return false;

  const ownerUid = String(row?.senderId || row?.createdBy || "").trim();
  const isOwner = ownerUid && ownerUid === uid;
  const isPrivate = isPrivateCapsule(row);
  const rowBusinessId = String(row?.businessId || "").trim();

  // Admins see every non-private capsule, but picking a business is a scope,
  // not a lens: while one is selected the answer must be "this business's
  // capsules" and nothing else. Without the businessId check here, the
  // member-uid reads below (which admit rows carrying no businessId at all)
  // leaked foreign capsules into a scoped admin's list — the business's own
  // members never saw them, because they fall through to the check below.
  if (isAdmin) {
    const scopedBusinessId = String(businessId || "").trim();
    if (!scopedBusinessId) return !isPrivate;
    return !isPrivate && rowBusinessId === scopedBusinessId;
  }
  if (accountType === ACCOUNT_TYPES.BUSINESS) {
    return !isPrivate && rowBusinessId === String(businessId || "").trim();
  }
  if (accountType === ACCOUNT_TYPES.INDIVIDUAL) return isPrivate && isOwner;
  return false;
}

function geohashEncode(lat, lng, precision = 9) {
  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let geohash = "";

  let latMin = -90;
  let latMax = 90;
  let lngMin = -180;
  let lngMax = 180;

  while (geohash.length < precision) {
    if (evenBit) {
      const lngMid = (lngMin + lngMax) / 2;
      if (lng >= lngMid) {
        idx = idx * 2 + 1;
        lngMin = lngMid;
      } else {
        idx = idx * 2;
        lngMax = lngMid;
      }
    } else {
      const latMid = (latMin + latMax) / 2;
      if (lat >= latMid) {
        idx = idx * 2 + 1;
        latMin = latMid;
      } else {
        idx = idx * 2;
        latMax = latMid;
      }
    }

    evenBit = !evenBit;
    if (++bit === 5) {
      geohash += BASE32.charAt(idx);
      bit = 0;
      idx = 0;
    }
  }

  return geohash;
}

function normalizeLocation(location) {
  const parseCoordinate = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" && !String(value).trim()) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const lat = parseCoordinate(location?.lat);
  const lng = parseCoordinate(location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  const placeId = String(location?.placeId || "").trim();
  const placeName = String(location?.placeName || "").trim();
  return {
    address: String(location?.address || "").trim(),
    lat,
    lng,
    ...(placeId ? { placeId } : {}),
    ...(placeName ? { placeName } : {}),
  };
}

function normalizeMedia(items = []) {
  const clean = (Array.isArray(items) ? items : [])
    .map((entry) => {
      const source = String(entry?.source || "").trim() || null;
      const campaignId = String(entry?.campaignId || "").trim();
      const campaignType = String(entry?.campaignType || "").trim();
      const width = Number(entry?.width);
      const height = Number(entry?.height);
      return {
        type: entry?.type === "video" ? "video" : "image",
        url: String(entry?.url || "").trim(),
        storagePath: String(entry?.storagePath || "").trim() || null,
        source,
        sessionPreviewUrl: String(entry?.sessionPreviewUrl || "").trim() || null,
        ...(source === "campaignHero" && campaignId ? { campaignId } : {}),
        ...(source === "campaignHero" && campaignType ? { campaignType } : {}),
        ...(source === "campaignHero" && Number.isFinite(width) && width > 0 ? { width } : {}),
        ...(source === "campaignHero" && Number.isFinite(height) && height > 0 ? { height } : {}),
      };
    })
    .filter((entry) => entry.url);
  const limited = clean.slice(0, MAX_MEDIA_ITEMS);
  const videoCount = limited.filter((entry) => entry.type === "video").length;
  if (videoCount > MAX_VIDEO_ITEMS) {
    throw new Error(`Only ${MAX_VIDEO_ITEMS} videos are allowed.`);
  }
  return limited;
}

function normalizeTimestampInput(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return null;
  return parsed;
}

function normalizePatch(patch = {}) {
  const out = {};

  if (patch.capsuleType != null) {
    const nextType = String(patch.capsuleType || "").trim();
    if (!TIME_CAPSULE_TYPES.includes(nextType)) {
      throw new Error("Invalid capsule type.");
    }
    out.capsuleType = nextType;
  }

  if (patch.status != null) {
    const nextStatus = String(patch.status || "").trim();
    if (!TIME_CAPSULE_STATUSES.includes(nextStatus)) {
      throw new Error("Invalid time capsule status.");
    }
    out.status = nextStatus;
    // An explicit status write is a deliberate decision, so the business-level
    // cascade must forget it ever drafted this capsule — otherwise bringing the
    // business live again would overrule the person who made that decision.
    out.businessStatusSync = null;
  }

  if (patch.messageTitle != null) out.messageTitle = String(patch.messageTitle || "").trim();
  if (patch.messageBody != null) out.messageBody = String(patch.messageBody || "").trim();
  if (patch.callToActionText != null) out.callToActionText = normalizeCtaText(patch.callToActionText);
  if (patch.copySource != null) out.copySource = normalizeCopySource(patch.copySource);
  if (patch.aiCopyBrief != null) out.aiCopyBrief = normalizeAiCopyBrief(patch.aiCopyBrief);
  if (patch.callToActionUrl != null) out.callToActionUrl = String(patch.callToActionUrl || "").trim();
  if (patch.callToActionCampaignId != null) out.callToActionCampaignId = String(patch.callToActionCampaignId || "").trim();
  if (patch.callToActionCampaignType != null) out.callToActionCampaignType = String(patch.callToActionCampaignType || "").trim();
  if (patch.callToActionCampaignPath != null) out.callToActionCampaignPath = String(patch.callToActionCampaignPath || "").trim();
  if (patch.callToActionBusinessId != null) out.callToActionBusinessId = String(patch.callToActionBusinessId || "").trim();
  if (patch.callToActionDestinationType != null) out.callToActionDestinationType = String(patch.callToActionDestinationType || "").trim();
  if (patch.placeName != null) out.placeName = String(patch.placeName || "").trim();
  if (patch.businessId !== undefined) out.businessId = normalizeBusinessId(patch.businessId);
  if (patch.hashtags != null) out.hashtags = normalizeHashtags(patch.hashtags);
  if (patch.recipientUserIds != null) out.recipientUserIds = [];
  if (patch.endDate !== undefined) out.endDate = normalizeTimestampInput(patch.endDate);
  if (patch.revealDate !== undefined) out.revealDate = normalizeTimestampInput(patch.revealDate);

  if (patch.location !== undefined) {
    const normalizedLocation = normalizeLocation(patch.location);
    out.location = normalizedLocation;
    out.geohash = normalizedLocation ? geohashEncode(normalizedLocation.lat, normalizedLocation.lng) : "";
  }

  // `undefined` (key absent from the patch) means "leave the stored link
  // alone" — an edit that doesn't touch location must not silently unlink an
  // already-linked capsule. `null` is a deliberate clear (manual placement).
  if (patch.businessLocationId !== undefined) {
    out.businessLocationId = normalizeBusinessLocationId(patch.businessLocationId);
  }

  // An explicit status or location write is a human decision; the marker
  // claims the location cascade owns this capsule's state, so it must go.
  if (patch.status !== undefined || patch.location !== undefined) {
    out.businessLocationSync = null;
  }

  if (patch.media !== undefined) {
    out.media = normalizeMedia(patch.media);
  }

  return out;
}

function isEqualValue(a, b) {
  if (a === b) return true;
  if (a?.seconds && b?.seconds) {
    return a.seconds === b.seconds && a.nanoseconds === b.nanoseconds;
  }
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function changedFields(fromDoc = {}, patch = {}) {
  return Object.keys(patch).filter((key) => !isEqualValue(fromDoc?.[key], patch?.[key]));
}

function assertCapsuleDates(nextDoc) {
  const isPublished = String(nextDoc?.status || "draft") === "published";
  if (nextDoc.capsuleType === "memberExclusive" && isPublished && !nextDoc.endDate) {
    throw new Error("memberExclusive capsules require an end date.");
  }
}

function sanitizeForCreate({
  capsuleType = "sponsored",
  status = "draft",
  messageTitle = "",
  messageBody = "",
  callToActionText = "",
  copySource = "default",
  aiCopyBrief = {},
  callToActionUrl = "",
  callToActionCampaignId = "",
  callToActionCampaignType = "",
  callToActionCampaignPath = "",
  callToActionBusinessId = "",
  callToActionDestinationType = "",
  businessId = null,
  hashtags = [],
  location = null,
  businessLocationId = null,
  revealDate = null,
  endDate = null,
  media = [],
}) {
  const normalizedType = TIME_CAPSULE_TYPES.includes(capsuleType) ? capsuleType : "sponsored";
  const normalizedStatus = TIME_CAPSULE_STATUSES.includes(status) ? status : "draft";
  const normalizedLocation = normalizeLocation(location);
  const normalizedReveal = normalizeTimestampInput(revealDate);
  const normalizedEndDate = normalizeTimestampInput(endDate);

  const payload = {
    capsuleType: normalizedType,
    status: normalizedStatus,
    messageTitle: String(messageTitle || "").trim(),
    messageBody: String(messageBody || "").trim(),
    callToActionText: normalizeCtaText(callToActionText),
    copySource: normalizeCopySource(copySource),
    aiCopyBrief: normalizeAiCopyBrief(aiCopyBrief),
    callToActionUrl: String(callToActionUrl || "").trim(),
    callToActionCampaignId: String(callToActionCampaignId || "").trim(),
    callToActionCampaignType: String(callToActionCampaignType || "").trim(),
    callToActionCampaignPath: String(callToActionCampaignPath || "").trim(),
    callToActionBusinessId: String(callToActionBusinessId || "").trim(),
    callToActionDestinationType: String(callToActionDestinationType || "").trim(),
    businessId: normalizeBusinessId(businessId),
    hashtags: normalizeHashtags(hashtags),
    location: normalizedLocation,
    businessLocationId: normalizeBusinessLocationId(businessLocationId),
    geohash: normalizedLocation ? geohashEncode(normalizedLocation.lat, normalizedLocation.lng) : "",
    revealDate: normalizedReveal,
    endDate: normalizedEndDate,
    media: normalizeMedia(media),
    recipientUserIds: [],
  };

  assertCapsuleDates(payload);
  return payload;
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function getGoogleMapsScriptUrl() {
  if (!GOOGLE_MAPS_API_KEY) return null;
  return `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
    GOOGLE_MAPS_API_KEY
  )}&libraries=places&v=weekly`;
}

export async function createTimeCapsuleDraft({ capsuleType = "sponsored", actor } = {}) {
  const { uid, businessId, isAdmin } = getActorContext(actor);
  if (!uid) throw new Error("You must be signed in to create a time capsule.");

  assertCreateTypeAllowed(capsuleType, actor);
  const payload = sanitizeForCreate({
    capsuleType,
    status: "draft",
    // A fresh draft has no reveal date chosen yet. `new Date()` here reads to
    // the create rule as "reveal in the past" (revealDate must be
    // >= request.time, the server's receipt time, which is always later than
    // a client-captured "now"), and denies every create. Omitting it lets
    // sanitizeForCreate's default (null) through instead, which the rule
    // explicitly allows.
    media: [],
    businessId,
  });

  const colRef = collection(db, "timeCapsules");
  const newDoc = await addDoc(colRef, {
    ...payload,
    createdByAdmin: Boolean(isAdmin),
    senderId: uid,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    modificationHistory: [],
  });
  const snap = await getDoc(newDoc);
  return toPlain(snap);
}

export async function duplicateTimeCapsule(sourceId, { actor } = {}) {
  const { uid, businessId, isAdmin } = getActorContext(actor);
  if (!uid) throw new Error("You must be signed in to duplicate a time capsule.");

  const sourceRef = doc(db, "timeCapsules", sourceId);
  const sourceSnap = await getDoc(sourceRef);
  if (!sourceSnap.exists()) throw new Error("Time capsule not found.");
  const source = toPlain(sourceSnap);

  assertCanAccessCapsule(source, actor);
  assertCreateTypeAllowed(source.capsuleType, actor);

  const now = new Date();
  const sourceReveal = toDateValue(source.revealDate);
  // A genuinely-future source reveal date carries over unchanged. Otherwise
  // (absent, or already in the past because the source capsule already
  // revealed) fall back to null rather than `now` — `now` is itself already
  // in the past by the time the write reaches the server, and the create
  // rule denies any revealDate that isn't >= request.time. Duplicating a
  // capsule that has already revealed must not be denied.
  const safeReveal = sourceReveal && sourceReveal.getTime() > now.getTime() ? sourceReveal : null;

  const payload = sanitizeForCreate({
    capsuleType: source.capsuleType,
    status: "draft",
    messageTitle: source.messageTitle || "",
    messageBody: source.messageBody || "",
    callToActionText: source.callToActionText || "",
    copySource: source.copySource || "default",
    aiCopyBrief: source.aiCopyBrief || {},
    callToActionUrl: source.callToActionUrl || "",
    callToActionCampaignId: source.callToActionCampaignId || "",
    callToActionCampaignType: source.callToActionCampaignType || "",
    callToActionCampaignPath: source.callToActionCampaignPath || "",
    callToActionBusinessId: source.callToActionBusinessId || "",
    callToActionDestinationType: source.callToActionDestinationType || "",
    businessId: source.businessId || businessId,
    hashtags: Array.isArray(source.hashtags) ? source.hashtags : [],
    location: source.location || null,
    // A duplicate keeps the location it copies, so it keeps that location's
    // link too — otherwise duplicating a linked capsule produces another
    // unlinked one, reintroducing the exact gap this field exists to close.
    businessLocationId: source.businessLocationId || null,
    revealDate: safeReveal,
    endDate: source.endDate || null,
    media: (Array.isArray(source.media) ? source.media : []).map((entry) => ({
      type: entry?.type === "video" ? "video" : "image",
      url: String(entry?.url || ""),
      storagePath: null,
    })),
  });

  const colRef = collection(db, "timeCapsules");
  const newDoc = await addDoc(colRef, {
    ...payload,
    createdByAdmin: Boolean(isAdmin),
    senderId: uid,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    modificationHistory: [],
  });

  const snap = await getDoc(newDoc);
  return toPlain(snap);
}

export async function getTimeCapsuleById(id, { actor } = {}) {
  const resolvedActor = getActorContext(actor);
  const refDoc = doc(db, "timeCapsules", id);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) return null;
  const row = toPlain(snap);
  assertCanAccessCapsule(row, resolvedActor);
  return row;
}

export async function listTimeCapsules({ mode = "all", actor } = {}) {
  const ctx = getActorContext(actor);
  const { uid, isAdmin, accountType, businessId } = ctx;
  if (!uid) return [];

  const colRef = collection(db, "timeCapsules");
  const forceBusiness =
    (isAdmin || accountType === ACCOUNT_TYPES.BUSINESS) &&
    Boolean(String(businessId || "").trim());
  const forceMine = !isAdmin && accountType === ACCOUNT_TYPES.INDIVIDUAL;
  const withNonPrivate = (candidate) =>
    isAdmin ? candidate : query(candidate, where("capsuleType", "in", NON_PRIVATE_CAPSULE_TYPES));

  let qy = withNonPrivate(query(colRef, orderBy("createdAt", "desc")));
  if (forceBusiness) {
    const normalizedBusinessId = String(businessId || "").trim();
    const businessSnaps = await getDocs(
      withNonPrivate(
        query(colRef, where("businessId", "==", normalizedBusinessId), orderBy("createdAt", "desc"))
      )
    );
    return businessSnaps.docs.map(toPlain).filter((row) => canActorSeeCapsule(row, ctx));
  } else if (mode === "mine" || forceMine) {
    qy = query(colRef, where("senderId", "==", uid), orderBy("createdAt", "desc"));
  }

  try {
    const snaps = await getDocs(qy);
    return snaps.docs.map(toPlain).filter((row) => canActorSeeCapsule(row, ctx));
  } catch (error) {
    if (mode === "all" && !forceMine) {
      const fallback = query(colRef, where("senderId", "==", uid), orderBy("createdAt", "desc"));
      const snaps = await getDocs(fallback);
      return snaps.docs.map(toPlain).filter((row) => canActorSeeCapsule(row, ctx));
    }
    throw error;
  }
}

export function subscribeTimeCapsules(
  { mode = "all", actor, pageSize, search = "", needsAttention = false } = {},
  onNext,
  onError
) {
  const ctx = getActorContext(actor);
  const { uid, isAdmin, accountType, businessId } = ctx;
  if (!uid) {
    onNext?.([], { hasMore: false });
    return () => {};
  }

  const colRef = collection(db, "timeCapsules");
  // Optional server-side cap. When set, every underlying query is limited to
  // `pageSize` newest docs so the list scales to thousands (the caller grows it
  // via "load more"). Absent → unlimited (legacy behavior for other callers).
  const capacity = Number(pageSize);
  const capped = Number.isFinite(capacity) && capacity > 0;
  const withLimit = (qy) => (capped ? query(qy, limit(capacity)) : qy);
  const emit = (rows, hasMore) => onNext?.(rows, { hasMore: Boolean(capped && hasMore) });

  // Server-side search. Firestore allows one array-contains per query, so this
  // is the most selective single token; the caller still filters the remaining
  // words over the (now much smaller) returned page.
  const { serverTerm } = toCapsuleSearchTerm(search);
  const withSearch = (qy) =>
    serverTerm ? query(qy, where("searchTokens", "array-contains", serverTerm)) : qy;

  // Admins are exempt: isAdmin() is document-independent, so the rules engine
  // proves their list statically without any filter, and a scoped admin is
  // entitled to see private capsules. Adding this to their branch would hide
  // rows they are supposed to see.
  const withNonPrivate = (qy) =>
    isAdmin ? qy : query(qy, where("capsuleType", "in", NON_PRIVATE_CAPSULE_TYPES));

  // Orphans: a capsule with no location. Explicitly `== null`, which is what
  // sanitizeForCreate writes when no location is set and what the location
  // delete cascade writes when it clears one. This runs server-side rather
  // than as a client predicate because the list is paged — a predicate over
  // one page would leave an orphan beyond it invisible, which is the exact
  // failure the filter exists to prevent.
  const withOrphansOnly = (qy) =>
    needsAttention ? query(qy, where("location", "==", null)) : qy;

  // A business selection scopes the read to that business for admins and
  // members alike. An admin keeps admin visibility within it — every status,
  // including rows a member's own permissions would hide.
  const forceBusiness =
    (isAdmin || accountType === ACCOUNT_TYPES.BUSINESS) &&
    Boolean(String(businessId || "").trim());
  const forceMine = !isAdmin && accountType === ACCOUNT_TYPES.INDIVIDUAL;
  // The "mine" branch pins senderId, which proves its own disjunct in the list
  // rule, so it needs no capsuleType filter — and must not have one, or an
  // individual account stops seeing its own private capsules.
  const primary = mode === "mine" || forceMine
      ? withLimit(withSearch(query(colRef, where("senderId", "==", uid), orderBy("createdAt", "desc"))))
      : withLimit(withSearch(withNonPrivate(query(colRef, orderBy("createdAt", "desc")))));
  const fallback = withLimit(
    withSearch(query(colRef, where("senderId", "==", uid), orderBy("createdAt", "desc")))
  );

  if (forceBusiness) {
    const normalizedBusinessId = String(businessId || "").trim();
    return onSnapshot(
      withLimit(
        withSearch(
          withOrphansOnly(
            withNonPrivate(
              query(colRef, where("businessId", "==", normalizedBusinessId), orderBy("createdAt", "desc"))
            )
          )
        )
      ),
      (snaps) =>
        emit(
          snaps.docs.map(toPlain).filter((row) => canActorSeeCapsule(row, ctx)),
          capped && snaps.docs.length >= capacity
        ),
      (err) => onError?.(err)
    );
  }

  let usingFallback = false;
  let unsubFallback = null;

  const unsubPrimary = onSnapshot(
    primary,
    (snaps) =>
      emit(
        snaps.docs.map(toPlain).filter((row) => canActorSeeCapsule(row, ctx)),
        capped && snaps.docs.length >= capacity
      ),
    (err) => {
      if (mode !== "all" || forceMine || usingFallback) {
        onError?.(err);
        return;
      }
      usingFallback = true;
      unsubFallback = onSnapshot(
        fallback,
        (snaps) =>
          emit(
            snaps.docs.map(toPlain).filter((row) => canActorSeeCapsule(row, ctx)),
            capped && snaps.docs.length >= capacity
          ),
        (fallbackErr) => onError?.(fallbackErr)
      );
    }
  );

  return () => {
    if (typeof unsubPrimary === "function") unsubPrimary();
    if (typeof unsubFallback === "function") unsubFallback();
  };
}

export async function updateTimeCapsule(id, patch = {}, { actor } = {}) {
  const { uid } = getActorContext(actor);
  if (!uid) throw new Error("You must be signed in to update a time capsule.");

  const refDoc = doc(db, "timeCapsules", id);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error("Time capsule not found.");
  const current = toPlain(snap);
  assertCanAccessCapsule(current, actor);
  assertCapsuleMutationAllowed(current, actor);

  const normalizedPatch = normalizePatch(patch);
  if ("capsuleType" in normalizedPatch) {
    assertCreateTypeAllowed(normalizedPatch.capsuleType, actor);
  } else {
    assertCreateTypeAllowed(current.capsuleType, actor);
  }

  delete normalizedPatch.senderId;
  delete normalizedPatch.createdByAdmin;
  normalizedPatch.recipientUserIds = [];

  const nextDoc = { ...current, ...normalizedPatch };
  if (!nextDoc.businessId) {
    const actorBusinessId = getActorContext(actor).businessId;
    if (actorBusinessId) {
      nextDoc.businessId = actorBusinessId;
      normalizedPatch.businessId = nextDoc.businessId;
    }
  }
  assertCapsuleDates(nextDoc);

  const modifiedFields = changedFields(current, normalizedPatch);
  const nextHistory = Array.isArray(current.modificationHistory) ? [...current.modificationHistory] : [];
  if (modifiedFields.length > 0) {
    nextHistory.push({
      modifiedAt: new Date(),
      modifiedBy: uid,
      modifiedFields,
    });
  }

  await updateDoc(refDoc, {
    ...normalizedPatch,
    senderId: current.senderId || uid,
    recipientUserIds: [],
    modificationHistory: nextHistory,
    updatedAt: serverTimestamp(),
  });

  const updated = await getDoc(refDoc);
  return toPlain(updated);
}

export async function setTimeCapsuleStatus(id, status, { actor } = {}) {
  const normalizedStatus = TIME_CAPSULE_STATUSES.includes(status) ? status : "draft";
  return updateTimeCapsule(id, { status: normalizedStatus }, { actor });
}

/**
 * Soft delete. The document moves to `deletedTimeCapsules` server-side, under
 * the same id, and the deletion is recorded in `auditEvents`. Permission is
 * enforced in the callable — rules block client deletes outright — and Storage
 * media is deliberately retained so a restore brings the images back with it.
 */
export async function deleteTimeCapsule(id, { actor, reason = null, surface = null } = {}) {
  const { uid } = getActorContext(actor);
  if (!uid) throw new Error("You must be signed in to delete a time capsule.");
  const capsuleId = String(id || "").trim();
  if (!capsuleId) throw new Error("Time capsule id is required.");
  await softDeleteDocumentCallable({
    entityType: "timeCapsule",
    entityId: capsuleId,
    reason,
    surface,
  });
  return { ok: true };
}

export async function restoreTimeCapsule(id) {
  const capsuleId = String(id || "").trim();
  if (!capsuleId) throw new Error("Time capsule id is required.");
  const result = await restoreDocumentCallable({
    entityType: "timeCapsule",
    entityId: capsuleId,
  });
  return result?.data || { ok: true, status: "draft" };
}

function inferMediaTypeFromFile(file) {
  const mime = String(file?.type || "").toLowerCase();
  return mime.startsWith("video/") ? "video" : "image";
}

function buildStoragePath(capsuleId, fileName = "file") {
  const safeName = String(fileName || "file").replace(/[^\w.-]/g, "_");
  return `timecapsules/${capsuleId}/${Date.now()}-${Math.floor(Math.random() * 1e9)}-${safeName}`;
}

async function uploadSingleMedia(capsuleId, file, onProgress) {
  const storagePath = buildStoragePath(capsuleId, file?.name);
  const fileRef = ref(storage, storagePath);
  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file);
    task.on(
      "state_changed",
      (snapshot) => {
        const total = Number(snapshot?.totalBytes || 0);
        const loaded = Number(snapshot?.bytesTransferred || 0);
        const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
        onProgress?.(progress);
      },
      (error) => reject(error),
      () => resolve(true)
    );
  });
  const url = await getDownloadURL(fileRef);
  return {
    type: inferMediaTypeFromFile(file),
    url,
    storagePath,
    source: "upload",
    sessionPreviewUrl: null,
  };
}

async function deleteStoragePaths(paths = []) {
  for (const path of paths) {
    if (!path) continue;
    try {
      await deleteObject(ref(storage, path));
    } catch (error) {
      const code = String(error?.code || "");
      if (!code.includes("object-not-found")) {
        throw error;
      }
    }
  }
}

export async function saveTimeCapsuleMedia(
  capsuleId,
  {
    existingMedia = [],
    nextMedia = [],
  } = {},
  { actor, onItemProgress } = {}
) {
  const currentExisting = normalizeMedia(existingMedia);
  if (currentExisting.length > MAX_MEDIA_ITEMS) {
    throw new Error(`Only ${MAX_MEDIA_ITEMS} media items are allowed.`);
  }

  const uploadQueue = (Array.isArray(nextMedia) ? nextMedia : []).slice(0, MAX_MEDIA_ITEMS);
  const resolved = [];
  for (let index = 0; index < uploadQueue.length; index += 1) {
    const entry = uploadQueue[index];
    if (entry?.file) {
      onItemProgress?.(index, 0, "uploading");
      const uploaded = await uploadSingleMedia(capsuleId, entry.file, (progress) => {
        onItemProgress?.(index, progress, "uploading");
      });
      onItemProgress?.(index, 100, "uploaded");
      resolved.push({
        ...uploaded,
        source: String(entry?.source || uploaded?.source || "").trim() || "upload",
        sessionPreviewUrl: String(entry?.sessionPreviewUrl || "").trim() || null,
      });
    } else if (entry?.url) {
      onItemProgress?.(index, 100, "retained");
      const source = String(entry?.source || "").trim() || null;
      const campaignId = String(entry?.campaignId || "").trim();
      const campaignType = String(entry?.campaignType || "").trim();
      const width = Number(entry?.width);
      const height = Number(entry?.height);
      resolved.push({
        type: entry?.type === "video" ? "video" : "image",
        url: String(entry.url),
        storagePath: String(entry.storagePath || "").trim() || null,
        source,
        sessionPreviewUrl: String(entry?.sessionPreviewUrl || "").trim() || null,
        ...(source === "campaignHero" && campaignId ? { campaignId } : {}),
        ...(source === "campaignHero" && campaignType ? { campaignType } : {}),
        ...(source === "campaignHero" && Number.isFinite(width) && width > 0 ? { width } : {}),
        ...(source === "campaignHero" && Number.isFinite(height) && height > 0 ? { height } : {}),
      });
    }
  }

  if (resolved.length > MAX_MEDIA_ITEMS) {
    throw new Error(`Only ${MAX_MEDIA_ITEMS} media items are allowed.`);
  }

  const keptStoragePaths = new Set(
    resolved.map((entry) => String(entry?.storagePath || "").trim()).filter(Boolean)
  );
  const removePaths = currentExisting
    .map((entry) => String(entry?.storagePath || "").trim())
    .filter(Boolean)
    .filter((path) => !keptStoragePaths.has(path));

  await updateTimeCapsule(capsuleId, { media: resolved }, { actor });
  await deleteStoragePaths(removePaths);

  return resolved;
}

export {
  MAX_HASHTAGS,
  MAX_MEDIA_ITEMS,
  MAX_VIDEO_ITEMS,
  TIME_CAPSULE_TYPES,
  TIME_CAPSULE_STATUSES,
  geohashEncode,
};
